// pages/Payments.tsx
import React, { useEffect, useState } from "react";
import { Download, Filter, Search } from "lucide-react";

// Import child components
import PaymentStatsCards from "../components/payments/PaymentStatsCards";
import PaymentTabs from "../components/payments/PaymentTabs";
import TransactionsTable from "../components/payments/TransactionsTable";
import PayoutsTable from "../components/payments/PayoutsTable";
import PaymentFilters from "../components/payments/PaymentFilters";
import WithdrawModal from "../components/payments/WithdrawModal";
import PaymentsSkeleton from "../components/payments/PaymentsSkeleton";

// Import hooks
import { useGetPayments } from "../lib/api/payments";
import { getDecodedJwt } from "../lib/auth";

const Payments: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const user = getDecodedJwt();

  const [activeTab, setActiveTab] = useState<"transactions" | "payouts">(
    "transactions"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch payments data
  const { data, isLoading } = useGetPayments(user?.id, {
    transactionStatus: activeTab === "transactions" ? statusFilter : undefined,
    payoutStatus: activeTab === "payouts" ? statusFilter : undefined,
    startDate: dateRange.start || undefined,
    endDate: dateRange.end || undefined,
    search: searchQuery || undefined,
  });

  const paymentStats = data?.stats || {
    availableBalance: 0,
    pendingAmount: 0,
    totalEarned: 0,
    payoutFailures: 0,
    pendingTransactions: 0,
    failedPayouts: 0,
  };

  const transactions = data?.transactions || [];
  const payouts = data?.payouts || [];

  if (isLoading) {
    return <PaymentsSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Payments
            </h1>
            <p className="text-gray-500 mt-1">
              Manage your earnings and payouts
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <PaymentStatsCards stats={paymentStats} />

        {/* Main Content Card */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Tabs and Actions Bar */}
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Tabs */}
              <PaymentTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                // transactionCount={transactions.length}
                // payoutCount={payouts.length}
              />

              {/* Search and Filter */}
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 lg:flex-initial">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full lg:w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all"
                  />
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2.5 rounded-xl border transition-all ${
                    showFilters
                      ? "border-[#8B52FF] bg-[#8B52FF]/5 text-[#8B52FF]"
                      : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  <Filter className="w-4 h-4" />
                </button>

                {/* Export */}
                <button className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 transition-all">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <PaymentFilters
                dateRange={dateRange}
                setDateRange={setDateRange}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                activeTab={activeTab}
              />
            )}
          </div>

          {/* Table Content */}
          <div className="overflow-x-auto">
            {activeTab === "transactions" ? (
              <TransactionsTable
                transactions={transactions}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
              />
            ) : (
              <PayoutsTable
                payouts={payouts}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
              />
            )}
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <WithdrawModal
          availableBalance={paymentStats.availableBalance}
          onClose={() => setShowWithdrawModal(false)}
        />
      )}
    </div>
  );
};

export default Payments;
